import { FC, useCallback, useEffect, useMemo } from 'react';

import { PermissionType, RoleType } from 'api/types/entity';
import {
  Action,
  ActionsList,
  ContentHeader,
  DialogConfirmResetPassword,
  DialogConfirmRevokeOtpVerification,
  SendCodeTimer,
} from 'components';
import { lodash, parseFullName } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { PermissionWrapper, usePermissions } from 'libs/permissions';
import { Chip, Stack } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';

import { useFormContext } from '../../forms/adminForm';

export interface AdminFormHeaderProps {
  isActive?: boolean;
  isDeactivated?: boolean;
  resendTime?: number;
  revokeOtpVerificationLoading?: boolean;
  resetPasswordLoading?: boolean;
  onResetPassword: () => Promise<any>;
  onResendInvite: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onRevokeOtpVerification?: () => Promise<any>;
}

interface ActionItem {
  value: ActionType;
  label: string;
  Icon: React.ElementType;
  color?: Colors;
}

enum ActionType {
  activate = 'activate',
  deactivate = 'deactivate',
  resetPassword = 'resetPassword',
  edit = 'edit',
  revokeOtpVerification = 'revokeOtpVerification',
}

export const AdminFormHeader: FC<AdminFormHeaderProps> = ({
  isActive,
  resendTime,
  isDeactivated,
  revokeOtpVerificationLoading,
  resetPasswordLoading,
  onResetPassword,
  onResendInvite,
  onActivate,
  onDeactivate,
  onRevokeOtpVerification,
}) => {
  const { readOnly, setReadOnly, resetForm, initialValues } = useFormContext();

  const { t } = useTranslation();

  const { checkPermissions } = usePermissions();

  const {
    isOpen: isOpenRevokeOtp,
    onClose: onCloseRevokeOtp,
    open: openRevokeOtp,
  } = useDialog();

  const {
    isOpen: isOpenResetPasswordDialog,
    onClose: onCloseResetPasswordDialog,
    open: openResetPasswordDialog,
  } = useDialog();

  const editable = !readOnly;
  const isSuperadmin = initialValues?.role.value === RoleType.Superadmin;
  const fullName = parseFullName(initialValues);

  const actions = useMemo<ActionItem[]>(
    () =>
      isDeactivated
        ? lodash.compact([
            checkPermissions([PermissionType.User_ActivateOne]) && {
              value: ActionType.activate,
              label: t('admin.admin.activate'),
              Icon: Icon.RotateRightCheck,
            },
          ])
        : lodash.compact([
            !isSuperadmin &&
              checkPermissions([PermissionType.User_DeactivateOne]) && {
                value: ActionType.deactivate,
                label: t('admin.admin.deactivate'),
                Icon: Icon.Bucket,
                color: Colors.red500,
              },
            isActive &&
              !isSuperadmin &&
              checkPermissions([PermissionType.User_RevokeOtpVerification]) &&
              onRevokeOtpVerification && {
                value: ActionType.revokeOtpVerification,
                label: t('common.revokeOtpVerification'),
                Icon: Icon.Lock,
              },
            isActive &&
              !isSuperadmin &&
              checkPermissions([PermissionType.Auth_ResetPassword]) && {
                value: ActionType.resetPassword,
                label: t('admin.admin.resetPassword'),
                Icon: Icon.Refresh,
              },
            checkPermissions([PermissionType.User_UpdateOne]) && {
              value: ActionType.edit,
              label: t('common.edit'),
              Icon: Icon.Edit,
            },
          ]),
    [
      isDeactivated,
      checkPermissions,
      t,
      isActive,
      isSuperadmin,
      onRevokeOtpVerification,
    ]
  );

  const onActionRevokeOtpVerification = useCallback(() => {
    openRevokeOtp();
  }, [openRevokeOtp]);

  const onConfirmRevokeOtp = useCallback(async () => {
    await onRevokeOtpVerification?.().finally(() => onCloseRevokeOtp());
  }, [onCloseRevokeOtp, onRevokeOtpVerification]);

  const onConfirmResetPassword = useCallback(async () => {
    await onResetPassword?.().finally(() => onCloseResetPasswordDialog());
  }, [onCloseResetPasswordDialog, onResetPassword]);

  const onClickOption = (action: ActionType) => {
    switch (action) {
      case ActionType.edit:
        setReadOnly(false);
        break;
      case ActionType.resetPassword:
        openResetPasswordDialog();
        break;
      case ActionType.activate:
        onActivate();
        break;
      case ActionType.deactivate:
        onDeactivate();
        break;
      case ActionType.revokeOtpVerification:
        onActionRevokeOtpVerification();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!editable) {
      resetForm();
    }
  }, [editable, resetForm]);

  return (
    <>
      <ContentHeader
        header={editable ? t('admin.admin.editTitle') : fullName}
        headerAfterContent={
          isDeactivated ? (
            <Chip size="s" text={t('admin.status.Deactivated')} variant="red" />
          ) : (
            <Chip size="s" text={t('admin.status.Active')} variant="green" />
          )
        }
        rightContent={
          editable ? null : (
            <Stack alignItems="center" direction="row" spacing={12}>
              {!isSuperadmin && !isActive && !isDeactivated && (
                <PermissionWrapper
                  permissions={[PermissionType.Auth_SendAccountActivationEmail]}
                >
                  <SendCodeTimer
                    buttonLabel={t('admin.admin.resendInvite')}
                    seconds={resendTime}
                    onClickSend={onResendInvite}
                  />
                </PermissionWrapper>
              )}
              {!!actions.length && (
                <ActionsList>
                  {actions.map((action) => (
                    <Action
                      key={action.value}
                      tooltip={action.label}
                      onClick={() => onClickOption(action.value)}
                    >
                      <action.Icon color={action.color} size={16} />
                    </Action>
                  ))}
                </ActionsList>
              )}
            </Stack>
          )
        }
      />

      <DialogConfirmRevokeOtpVerification
        fullName={fullName}
        isOpen={isOpenRevokeOtp}
        loading={revokeOtpVerificationLoading}
        onClose={onCloseRevokeOtp}
        onConfirm={onConfirmRevokeOtp}
      />

      <DialogConfirmResetPassword
        fullName={fullName}
        isOpen={isOpenResetPasswordDialog}
        loading={resetPasswordLoading}
        onClose={onCloseResetPasswordDialog}
        onConfirm={onConfirmResetPassword}
      />
    </>
  );
};
